import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Submission = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    collection: '',
    summary: '',
    intentionNote: '',
    additionalInfo: '',
    targetAudience: '',
    biography: '',
    consent: false
  });

  const [files, setFiles] = useState({
    manuscript: null,
    coverPage: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast({
        title: "Consentement requis",
        description: "Veuillez accepter les conditions de traitement des données",
        variant: "destructive",
      });
      return;
    }

    if (!files.manuscript) {
      toast({
        title: "Manuscrit requis",
        description: "Veuillez joindre votre manuscrit",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert files to base64
      const manuscriptBase64 = await fileToBase64(files.manuscript);
      const coverPageBase64 = files.coverPage ? await fileToBase64(files.coverPage) : null;

      const response = await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          bio: formData.biography,
          title: formData.title,
          genre: formData.collection,
          summary: formData.summary,
          intention_note: formData.intentionNote,
          additional_info: formData.additionalInfo,
          target_audience: formData.targetAudience,
          manuscript_filename: files.manuscript.name,
          manuscript_data: manuscriptBase64,
          cover_page_filename: files.coverPage?.name || null,
          cover_page_data: coverPageBase64,
          consent: formData.consent
        })
      });

      if (!response.ok) throw new Error('Submission failed');
      
      toast({
        title: "Succès !",
        description: t.submission.form.success,
      });
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast({
        title: "Erreur",
        description: t.submission.form.error,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <div className="min-h-screen pt-16 bg-hermes-400">
      {/* Logo fixe en haut à gauche */}
      <div className="fixed top-24 left-8 z-40">
        <img 
          src="/logo-quid-novi.png" 
          alt="Quid Novi" 
          className="h-20 w-20"
        />
      </div>

      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/authors" 
            className="inline-flex items-center space-x-2 text-white hover:text-white/80 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à l'espace auteurs</span>
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            {t.submission.title}
          </h1>
          <p className="text-lg text-white/90">
            {t.submission.subtitle}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-xl shadow-lg border border-neutral-200">
            {/* Personal Information */}
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6 flex items-center">
                <div className="w-8 h-8 bg-hermes-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                  1
                </div>
                Informations personnelles
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.firstName} *
                  </label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.lastName} *
                  </label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.email} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.phone}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Book Information */}
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6 flex items-center">
                <div className="w-8 h-8 bg-hermes-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                  2
                </div>
                Informations sur l'ouvrage
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.title} *
                  </label>
                  <Input
                    id="title"
                    name="title"
                    type="text"
                    required
                    value={formData.title}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="collection" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.collection} *
                  </label>
                  <Select
                    value={formData.collection}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, collection: value }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choisir une collection" />
                    </SelectTrigger>
                    <SelectContent>
                      {t.submission.form.collections.map((collection) => (
                        <SelectItem key={collection} value={collection}>
                          {collection}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="summary" className="block text-sm font-medium text-neutral-700 mb-2">
                    Résumé / Note d'intention *
                  </label>
                  <Textarea
                    id="summary"
                    name="summary"
                    rows={5}
                    required
                    maxLength={2000}
                    value={formData.summary}
                    onChange={handleChange}
                    className="resize-none"
                    placeholder="Présentez brièvement votre ouvrage : sujet, angle d'approche, originalité..."
                  />
                  <p className="text-sm text-neutral-500 mt-1">
                    {formData.summary.length} / 2000 caractères
                  </p>
                </div>

                <div>
                  <label htmlFor="intentionNote" className="block text-sm font-medium text-neutral-700 mb-2">
                    Note d'intention détaillée
                  </label>
                  <Textarea
                    id="intentionNote"
                    name="intentionNote"
                    rows={4}
                    maxLength={2000}
                    value={formData.intentionNote}
                    onChange={handleChange}
                    className="resize-none"
                    placeholder="Pourquoi avez-vous écrit cet ouvrage ? Quel message souhaitez-vous transmettre ?"
                  />
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-neutral-700 mb-2">
                    Autre point à communiquer
                  </label>
                  <Textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    rows={3}
                    maxLength={1000}
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    className="resize-none"
                    placeholder="Toute information complémentaire que vous souhaitez nous communiquer..."
                  />
                </div>

                <div>
                  <label htmlFor="targetAudience" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.targetAudience} *
                  </label>
                  <Input
                    id="targetAudience"
                    name="targetAudience"
                    type="text"
                    required
                    value={formData.targetAudience}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="keywords" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t.submission.form.keywords}
                  </label>
                  <Input
                    id="keywords"
                    name="keywords"
                    type="text"
                    value={formData.keywords}
                    onChange={handleChange}
                    placeholder="mot1, mot2, mot3"
                  />
                </div>

                <div>
                  <label htmlFor="biography" className="block text-sm font-medium text-neutral-700 mb-2">
                    Votre biographie / CV *
                  </label>
                  <Textarea
                    id="biography"
                    name="biography"
                    rows={5}
                    required
                    value={formData.biography}
                    onChange={handleChange}
                    className="resize-none"
                    placeholder="Présentez-vous : parcours, expérience, publications précédentes, domaines d'expertise..."
                  />
                </div>
              </div>
            </div>

            {/* File Uploads */}
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6 flex items-center">
                <div className="w-8 h-8 bg-hermes-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                  3
                </div>
                Documents
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    {t.submission.form.manuscript} *
                  </label>
                  <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 hover:border-hermes-400 transition-colors">
                    <input
                      type="file"
                      id="manuscript"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'manuscript')}
                      className="hidden"
                    />
                    <label
                      htmlFor="manuscript"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      {files.manuscript ? (
                        <div className="text-center">
                          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                          <p className="text-neutral-900 font-medium">{files.manuscript.name}</p>
                          <p className="text-sm text-neutral-500 mt-1">Cliquez pour changer</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
                          <p className="text-neutral-700 font-medium">Cliquez pour télécharger votre manuscrit</p>
                          <p className="text-sm text-neutral-500 mt-1">PDF ou DOCX</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    {t.submission.form.coverPage}
                  </label>
                  <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 hover:border-hermes-400 transition-colors">
                    <input
                      type="file"
                      id="coverPage"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'coverPage')}
                      className="hidden"
                    />
                    <label
                      htmlFor="coverPage"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      {files.coverPage ? (
                        <div className="text-center">
                          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                          <p className="text-neutral-900 font-medium">{files.coverPage.name}</p>
                          <p className="text-sm text-neutral-500 mt-1">Cliquez pour changer</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
                          <p className="text-neutral-700 font-medium">Cliquez pour télécharger la 4e de couverture</p>
                          <p className="text-sm text-neutral-500 mt-1">PDF ou DOCX (optionnel)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Consent */}
            <div className="mb-8">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                  className="mt-1"
                />
                <label htmlFor="consent" className="text-sm text-neutral-700 leading-relaxed cursor-pointer">
                  {t.submission.form.consent}
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !formData.consent}
              className="w-full bg-hermes-600 hover:bg-hermes-700 text-white py-6 text-lg font-medium"
            >
              {isSubmitting ? 'Envoi en cours...' : t.submission.form.submit}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Submission;
